import * as THREE from 'three';
import { createMeteorRockGeometry, LUNAR_ASSET_COLORS } from '../assets/LunarAssetKit';

export type MeteorResources = {
  readonly rockGeometry: THREE.IcosahedronGeometry;
  readonly rockMaterials: readonly [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial];
};

export function createMeteorResources(): MeteorResources {
  const rockMaterials: [THREE.MeshStandardMaterial, THREE.MeshStandardMaterial] = [
    new THREE.MeshStandardMaterial({
      color: LUNAR_ASSET_COLORS.meteor,
      roughness: 0.98,
      metalness: 0.015,
      flatShading: true,
      vertexColors: true,
    }),
    new THREE.MeshStandardMaterial({
      color: LUNAR_ASSET_COLORS.meteorWarm,
      roughness: 0.98,
      metalness: 0.015,
      flatShading: true,
      vertexColors: true,
    }),
  ];

  return {
    rockGeometry: createMeteorRockGeometry(0.24, 3, 11),
    rockMaterials,
  };
}

export function disposeMeteorResources(resources: MeteorResources): void {
  resources.rockGeometry.dispose();
  resources.rockMaterials.forEach((material) => material.dispose());
}

export class Meteor {
  readonly group = new THREE.Group();
  readonly target: THREE.Vector3;
  readonly index: number;
  readonly targetIndex: number;
  readonly revealsCrater: boolean;

  private readonly rock: THREE.Mesh;
  private readonly direction = new THREE.Vector3();
  private readonly start: THREE.Vector3;
  private readonly duration: number;
  private elapsed = 0;
  private impacted = false;

  constructor(
    index: number,
    start: THREE.Vector3,
    target: THREE.Vector3,
    delay: number,
    scale: number,
    resources: MeteorResources,
    options: { duration?: number; revealsCrater?: boolean; targetIndex?: number } = {},
  ) {
    this.index = index;
    this.targetIndex = options.targetIndex ?? index;
    this.revealsCrater = options.revealsCrater ?? true;
    this.start = start.clone();
    this.target = target.clone();
    this.duration = options.duration ?? 1.02 + index * 0.08;
    this.elapsed = -delay;

    this.rock = new THREE.Mesh(resources.rockGeometry, resources.rockMaterials[index % 2]);
    this.rock.scale.setScalar(scale);
    this.rock.castShadow = true;

    this.group.add(this.rock);
    this.group.visible = false;
    this.direction.copy(this.target).sub(this.start).normalize();
    this.group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.direction);
    this.group.position.copy(this.start);
  }

  update(delta: number, reducedMotion: boolean): boolean {
    if (this.impacted) return false;
    this.elapsed += reducedMotion ? delta * 2 : delta;
    if (this.elapsed < 0) {
      this.group.visible = false;
      return false;
    }

    this.group.visible = true;
    const progress = THREE.MathUtils.clamp(this.elapsed / this.duration, 0, 1);
    const eased = 1 - (1 - progress) ** 3;
    this.group.position.lerpVectors(this.start, this.target, eased);
    this.rock.rotation.x += delta * 4.6;
    this.rock.rotation.z += delta * 3.2;

    if (progress >= 1) {
      this.impacted = true;
      this.group.visible = false;
      return true;
    }
    return false;
  }

  setTarget(target: THREE.Vector3): void {
    this.target.copy(target);
    this.direction.copy(this.target).sub(this.start).normalize();
    this.group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.direction);
  }

  dispose(): void {
    this.group.clear();
  }
}
